import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import ContactLeadModal, { ContactLeadChannel } from '../../public/ContactLeadModal';
import { usePublicSitePath } from '../../../pages/public/publicSitePaths';
import { Vehicle, formatMileage, formatPrice, getVehicleImage } from '../WebsiteTemplate';

interface VehicleCardPublicProps {
  vehicle: Vehicle;
  ctaLabel: string;
  /** Tenant's call number (from website settings). Chip hidden when absent. */
  callPhone?: string;
  /** Tenant's SMS/text number. Falls back to callPhone; chip hidden when both absent. */
  textPhone?: string;
  /** Tenant's WhatsApp number. Falls back to textPhone then callPhone. */
  whatsappPhone?: string;
  /** URL of an uploaded BHPH badge image. When set, renders an <img> instead of the text badge. */
  badgeImageUrl?: string;
}

const VehicleCardPublic: React.FC<VehicleCardPublicProps> = ({
  vehicle,
  ctaLabel,
  callPhone,
  textPhone,
  whatsappPhone,
  badgeImageUrl,
}) => {
  const imageUrl = getVehicleImage(vehicle);
  const publicSitePath = usePublicSitePath();
  const [leadChannel, setLeadChannel] = useState<ContactLeadChannel | null>(null);

  // Build contact actions from the tenant's own number(s). No hardcoded fallback —
  // a chip is simply not rendered (honest absence) when its number is unset, so
  // one tenant never shows another tenant's contact details.
  const callHref = callPhone ? `tel:${callPhone.replace(/[^0-9+]/g, '')}` : null;
  const smsSource = textPhone || callPhone;
  const waSource = whatsappPhone || textPhone || callPhone;
  const hasContactCta = Boolean(callHref || smsSource || waSource);
  const vehicleLabel = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;

  return (
    <>
      <article className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 flex flex-col">
        {/* Image */}
        <div className="relative aspect-[16/10] overflow-hidden bg-gray-100 flex-shrink-0">
          <img
            src={imageUrl}
            alt={vehicleLabel}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/images/vehicle_placeholder.svg';
            }}
          />
          {/* Available / BHPH badge — show one or the other, not both */}
          {vehicle.bhph_eligible ? (
            badgeImageUrl ? (
              <span className="absolute top-3 left-3 z-10 inline-flex items-center rounded-lg bg-white/95 p-1.5 shadow-md ring-1 ring-black/10">
                <img
                  src={badgeImageUrl}
                  alt="In-House Financing"
                  className="h-10 w-auto"
                  onError={(e) => {
                    const chip = (e.target as HTMLImageElement).parentElement;
                    if (chip) chip.style.display = 'none';
                  }}
                />
              </span>
            ) : (
              <span className="absolute top-3 left-3 inline-flex items-center rounded-full bg-green-600 text-white px-2.5 py-1 text-xs font-semibold shadow-sm z-10">
                In-House Financing
              </span>
            )
          ) : (
            <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              Available Now
            </span>
          )}
          {/* Price badge — only shown when price is known */}
          {vehicle.price && (
            <span
              className="absolute bottom-3 right-3 text-white text-lg font-bold px-3 py-1 rounded-lg"
              style={{ backgroundColor: '#DC2626', fontFamily: "'Oswald', sans-serif" }}
            >
              {formatPrice(vehicle.price)}
            </span>
          )}
        </div>

        {/* Body */}
        <div className="p-4 flex flex-col flex-grow">
          <h3
            className="text-lg font-bold text-gray-900 mb-1"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            {vehicleLabel}
            {vehicle.trim && (
              <span className="text-gray-500 font-normal text-sm ml-1">{vehicle.trim}</span>
            )}
          </h3>

          {/* Spacer pushes KPIs + CTAs to bottom */}
          <div className="flex-grow" />

          <div className="flex items-center gap-4 text-sm text-gray-600 mb-1">
            {vehicle.mileage && <span>{formatMileage(vehicle.mileage)}</span>}
            {vehicle.exterior_color && <span>{vehicle.exterior_color}</span>}
            {vehicle.transmission && <span>{vehicle.transmission}</span>}
          </div>

          {vehicle.bhph_eligible && vehicle.bhph_payment_amount && (
            <p className="text-xs text-green-700 mb-4">
              From ${vehicle.bhph_payment_amount?.toLocaleString()}/
              {vehicle.bhph_payment_frequency === 'biweekly' ? 'bi-weekly' : 'mo'}
              {vehicle.bhph_min_down_type === 'percent'
                ? ` | ${vehicle.bhph_min_down_value}% min down`
                : vehicle.bhph_min_down_value
                  ? ` | $${vehicle.bhph_min_down_value.toLocaleString()} min down`
                  : ''}
            </p>
          )}
          {(!vehicle.bhph_eligible || !vehicle.bhph_payment_amount) && <div className="mb-4" />}

          {/* CTAs — Call stays direct; Text/WhatsApp first capture as tracked leads. */}
          {hasContactCta && (
            <div className="grid grid-cols-3 gap-2 mb-2">
              {callHref && (
                <a
                  href={callHref}
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-blue-700 hover:bg-blue-800 py-2.5 text-sm font-bold text-white transition-colors"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>{' '}
                  Call
                </a>
              )}
              {smsSource && (
                <button
                  type="button"
                  onClick={() => setLeadChannel('text')}
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-gray-700 hover:bg-gray-800 py-2.5 text-sm font-bold text-white transition-colors"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  <span>💬</span> Text
                </button>
              )}
              {waSource && (
                <button
                  type="button"
                  onClick={() => setLeadChannel('whatsapp')}
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-green-600 hover:bg-green-700 py-2.5 text-sm font-bold text-white transition-colors"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  WhatsApp
                </button>
              )}
            </div>
          )}
          <Link
            to={publicSitePath(`/inventory/${vehicle.vin || vehicle.id}`)}
            className="flex items-center justify-center rounded-lg border-2 border-gray-300 hover:border-blue-600 hover:text-blue-600 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors"
          >
            {ctaLabel}
          </Link>
        </div>
      </article>

      {leadChannel && (
        <ContactLeadModal
          open
          channel={leadChannel}
          onClose={() => setLeadChannel(null)}
          contactPhone={smsSource}
          contactWhatsapp={waSource}
          vehicleId={vehicle.id}
          vehicleLabel={vehicleLabel}
        />
      )}
    </>
  );
};

export default VehicleCardPublic;
